import { DownOutlined, StarOutlined } from "@ant-design/icons";
import { Button, Dropdown, Grid } from "antd";
import { MonitorModal } from "components/Assessment/components/Monitors";
import { SaveSearchFormValues } from "components/Assessment/components/Monitors";
import { useMonitors } from "components/Assessment/contexts/MonitorsContext";
import { useControls } from "components/Assessment/hooks/useControls";
import { GraphTooltip } from "components/GraphTooltip";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";

export const CreateMonitorButton: React.FC<{
  disabled: boolean;
  addSearch: (name: string, searchTerm: string, show: string) => void;
  showTemplateOptions?: boolean;
}> = ({ disabled, addSearch, showTemplateOptions = true }) => {
  const { controls } = useControls();
  const { md } = Grid.useBreakpoint();
  const [saveSearchModalOpen, setSaveSearchModalOpen] = useState(false);
  const [createMonitorModalOpen, setCreateMonitorModalOpen] = useState(false);
  const [stagedWhere, setStagedWhere] = useState<string>("");
  const [searchParams, setSearchParams] = useSearchParams();
  const { monitors, loading: monitorsLoading } = useMonitors();
  const [selectedTemplateId, setSelectedTemplateId] = useState<
    string | undefined
  >();

  const openSaveSearchModal = useCallback(() => {
    setSaveSearchModalOpen(true);
    setStagedWhere(controls.where);
  }, [controls.where]);

  // Handle URL parameters for template creation and modal shortcuts
  useEffect(() => {
    const templateId = searchParams.get("templateId") ?? "";
    const openSaveSearch = searchParams.get("openSaveSearch");
    const createMonitorParam = searchParams.get("createMonitor");
    const templateMode = searchParams.get("templateMode");
    const show = searchParams.get("show");

    // If we have a templateId parameter, wait for monitors to finish loading
    if (templateId && monitorsLoading) {
      return;
    }

    const templateMonitor =
      templateId && monitors ? monitors[templateId] : undefined;
    const shouldOpenModal =
      openSaveSearch === "true" ||
      createMonitorParam === "true" ||
      templateMonitor;

    if (!shouldOpenModal) {
      return;
    }

    const nextFields: Partial<SaveSearchFormValues> = {};

    if (templateMonitor) {
      // Duplicating a specific template - open create monitor modal with values pre-filled
      nextFields.where = templateMonitor.searchTerm;
      nextFields.label = `Copy of ${templateMonitor.label}`;
      setSelectedTemplateId(templateId); // Set the specific template ID
      setStagedWhere(templateMonitor.searchTerm);
      setCreateMonitorModalOpen(true);
      setSaveSearchModalOpen(false);
    } else {
      setSelectedTemplateId(templateMode === "true" ? "" : undefined);
      nextFields.where = controls.where;
      setStagedWhere(nextFields.where ?? controls.where);
      if (createMonitorParam === "true") {
        setCreateMonitorModalOpen(true);
        setSaveSearchModalOpen(false);
      } else {
        setSaveSearchModalOpen(true);
      }
    }

    const newParams = new URLSearchParams(searchParams);
    newParams.delete("templateId");
    newParams.delete("openSaveSearch");
    newParams.delete("createMonitor");
    newParams.delete("templateMode");
    if (show) {
      newParams.set("show", show);
    }
    setSearchParams(newParams, { replace: true });
  }, [
    searchParams,
    monitors,
    monitorsLoading,
    controls.where,
    setSearchParams,
  ]);

  const closeSaveSearchModal = useCallback(() => {
    setSaveSearchModalOpen(false);
    setSelectedTemplateId(undefined);
  }, []);

  const closeCreateMonitorModal = useCallback(() => {
    setCreateMonitorModalOpen(false);
    setSelectedTemplateId(undefined);
  }, []);

  const handleSaveSearch = useCallback(
    async (values: SaveSearchFormValues) => {
      addSearch(values.label, values.where, controls.show);
      closeSaveSearchModal();
    },
    [closeSaveSearchModal, addSearch, controls.show]
  );

  const handleSaveSearchFromModal = useCallback(
    (name: string, searchTerm: string, _show: string) => {
      void handleSaveSearch({ label: name, where: searchTerm });
    },
    [handleSaveSearch]
  );

  const tooltipText =
    controls.where === ""
      ? "No search term"
      : "Save this search for later use or monitoring";

  const handleCreateFromScratch = useCallback(() => {
    setSelectedTemplateId(undefined);
    openSaveSearchModal();
  }, [openSaveSearchModal]);

  const handleCreateFromTemplate = useCallback(() => {
    // Open modal with template selection mode
    setSelectedTemplateId(""); // Empty string to show template selector
    openSaveSearchModal();
  }, [openSaveSearchModal]);

  const menuItems = useMemo(() => {
    const items = [
      {
        key: "scratch",
        label: "Create from Scratch",
        onClick: handleCreateFromScratch,
      },
    ];

    if (showTemplateOptions) {
      items.push({
        key: "template",
        label: "Create from Template",
        onClick: handleCreateFromTemplate,
      });
    }

    return items;
  }, [handleCreateFromScratch, handleCreateFromTemplate, showTemplateOptions]);

  const triggerButton = (
    <Button
      disabled={disabled || !controls.where}
      icon={<StarOutlined />}
      onClick={showTemplateOptions ? undefined : openSaveSearchModal}
    >
      {!md ? "" : "Save Search"}
      {showTemplateOptions && <DownOutlined style={{ marginLeft: 4 }} />}
    </Button>
  );

  const allowTemplateSelectionForSave = useMemo(
    () => showTemplateOptions || selectedTemplateId !== undefined,
    [showTemplateOptions, selectedTemplateId]
  );

  const startWithTemplateForSave = useMemo(
    () => selectedTemplateId === "",
    [selectedTemplateId]
  );

  return (
    <>
      <GraphTooltip title={tooltipText}>
        {showTemplateOptions ? (
          <Dropdown
            menu={{ items: menuItems }}
            disabled={disabled || !controls.where}
          >
            {triggerButton}
          </Dropdown>
        ) : (
          triggerButton
        )}
      </GraphTooltip>
      {saveSearchModalOpen && (
        <MonitorModal
          mode="save-search"
          open={saveSearchModalOpen}
          onCancel={closeSaveSearchModal}
          initialSearchTerm={stagedWhere || controls.where}
          show={controls.show}
          onSaveSearch={handleSaveSearchFromModal}
          allowTemplateSelection={allowTemplateSelectionForSave}
          startWithTemplate={startWithTemplateForSave}
        />
      )}
      {createMonitorModalOpen && (
        <MonitorModal
          mode="create-monitor"
          open={createMonitorModalOpen}
          onCancel={closeCreateMonitorModal}
          initialSearchTerm={
            selectedTemplateId && monitors && monitors[selectedTemplateId]
              ? monitors[selectedTemplateId].searchTerm
              : stagedWhere || controls.where
          }
          show={
            selectedTemplateId && monitors && monitors[selectedTemplateId]
              ? monitors[selectedTemplateId].show
              : controls.show
          }
          allowTemplateSelection={
            selectedTemplateId === "" || showTemplateOptions
          }
          startWithTemplate={selectedTemplateId === ""}
          {...(selectedTemplateId &&
          selectedTemplateId !== "" &&
          monitors &&
          monitors[selectedTemplateId]
            ? {
                templateValues: monitors[selectedTemplateId],
                templateKey: selectedTemplateId,
              }
            : {})}
        />
      )}
    </>
  );
};
